import { createContext, useMemo, useReducer, useContext, ReactNode } from "react"
import { produce } from "immer"
import { usePreferences } from "hooks/use-preferences"

const PREFERENCE_SUBTYPE = "graph-risk"
const PREFERENCE_FIELDS = ["selectedCategory"]

interface RiskState {
  selectedCategory: string
}

const DEFAULT_STATE: RiskState = {
  selectedCategory: "total",
}

type RiskAction =
  | { type: "setSelectedCategory", selectedCategory: string }
  | { type: "setPreferences", preferences: Partial<RiskState> | null }

function reducer(current: RiskState, action: RiskAction): RiskState {
  return produce(current, draft => {
    switch (action.type) {
      case "setSelectedCategory":
        draft.selectedCategory = action.selectedCategory
        break
      case "setPreferences":
        if (action.preferences) {
          Object.assign(draft, action.preferences)
        }
        break
    }
  })
}

function setPreferences(preferences: Partial<RiskState>): RiskAction {
  return { type: "setPreferences", preferences }
}

export interface RiskContextValue extends RiskState {
  isPreferencesLoaded: boolean
  setSelectedCategory: (selectedCategory: string) => void
}

export const RISK_CONTEXT = createContext<RiskContextValue | undefined>(undefined)

export function useRiskContext(): RiskContextValue {
  const ctx = useContext(RISK_CONTEXT)
  if (ctx === undefined) {
    throw new Error("useRiskContext must be used within a RiskContext provider")
  }
  return ctx
}

interface RiskContextProps {
  children: ReactNode
}

export default function RiskContext({ children }: RiskContextProps) {
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE)
  const isPreferencesLoaded = usePreferences(
    PREFERENCE_SUBTYPE,
    PREFERENCE_FIELDS,
    state,
    dispatch,
    setPreferences,
  )

  const contextUpdaters = useMemo(() => ({
    setSelectedCategory: (selectedCategory: string) => dispatch({ type: "setSelectedCategory", selectedCategory }),
  }), [dispatch])

  const contextValue = useMemo<RiskContextValue>(() => ({
    ...state,
    ...contextUpdaters,
    isPreferencesLoaded,
  }), [state, contextUpdaters, isPreferencesLoaded])

  return <RISK_CONTEXT.Provider value={contextValue}>
    {children}
  </RISK_CONTEXT.Provider>
}
